import { Trans } from '@lingui/react/macro';
import { TrendingUp, Lock } from 'lucide-react';

import { Card, CardContent } from '~/components/ui/card';

interface AnalysisData {
  fillerWords?: string[];
  score?: number | null;
  speechPace?: string | null;
  clarity?: string | null;
  sentiment?: string | null;
  videoDurationSeconds?: number;
  personalizedTips?: string[];
  questionFeedback?: Array<{
    strengths?: string[];
    weaknesses?: string[];
  }>;
}

interface ReadinessScoreProps {
  analysisData: AnalysisData | null;
  isLocked?: boolean;
  onUnlock?: () => void;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function calculateReadiness(data: AnalysisData): number {
  let score = 50;

  // Clarity bonus
  if (data.clarity === 'Clear') score += 15;
  else if (data.clarity === 'Muffled' || data.clarity === 'Unclear') score -= 10;

  // Pace bonus
  if (data.speechPace === 'Moderate') score += 10;
  else if (data.speechPace === 'Fast' || data.speechPace === 'Slow') score -= 5;

  // Filler words penalty
  const fillerCount = data.fillerWords?.length || 0;
  score -= Math.min(fillerCount * 3, 20);

  // Sentiment bonus
  if (data.sentiment === 'Positive') score += 10;
  else if (data.sentiment === 'Negative') score -= 10;

  // Duration consideration (ideal: 60-90 seconds)
  const duration = data.videoDurationSeconds || 0;
  if (duration >= 60 && duration <= 90) score += 5;
  else if (duration > 120) score -= 5;
  else if (duration < 30 && duration > 0) score -= 10;

  // Base score adjustment
  const baseScore = data.score ?? 5;
  score += (baseScore - 5) * 3;

  return clamp(Math.round(score), 0, 100);
}

function calculateSeniority(data: AnalysisData): number {
  let score = 5;

  // Clarity is heavily weighted for senior presence
  if (data.clarity === 'Clear') score += 2;
  else if (data.clarity === 'Muffled' || data.clarity === 'Unclear') score -= 1.5;

  // Pace control shows experience
  if (data.speechPace === 'Moderate') score += 1;
  else if (data.speechPace === 'Fast') score -= 0.5;

  // Fewer filler words = more senior
  const fillerCount = data.fillerWords?.length || 0;
  if (fillerCount === 0) score += 1.5;
  else if (fillerCount <= 2) score += 0.5;
  else if (fillerCount > 5) score -= 1;

  // Confident sentiment
  if (data.sentiment === 'Positive') score += 0.5;
  else if (data.sentiment === 'Negative') score -= 0.5;

  // Base score factor
  const baseScore = data.score ?? 5;
  score += (baseScore - 5) * 0.3;

  return clamp(Math.round(score * 10) / 10, 0, 10);
}

function getReadinessLabel(score: number): string {
  if (score >= 80) return 'Excellent';
  if (score >= 65) return 'Good';
  if (score >= 50) return 'Moderate';
  if (score >= 35) return 'Needs Work';
  return 'Not Ready';
}

function getSeniorityLabel(score: number): string {
  if (score >= 8) return 'Senior';
  if (score >= 6) return 'Mid-Level';
  if (score >= 4) return 'Junior';
  return 'Entry';
}

export function ReadinessScore({
  analysisData,
  isLocked = false,
  onUnlock,
}: ReadinessScoreProps) {
  if (!analysisData) return null;

  const readiness = calculateReadiness(analysisData);
  const seniority = calculateSeniority(analysisData);

  if (isLocked) {
    return (
      <Card className="border-2 border-brand-gold/20 bg-gradient-to-br from-brand-gold/5 to-white overflow-hidden">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-gold/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-brand-gold/50" />
            </div>
            <h3 className="text-sm font-bold text-brand-dark-blue flex items-center gap-2">
              <Trans>Interview Readiness</Trans>
              <Lock className="w-3.5 h-3.5 text-brand-gold" />
            </h3>
          </div>

          <div className="relative">
            <div className="flex gap-6 blur-sm select-none pointer-events-none">
              {/* Circular gauge placeholder */}
              <div className="flex flex-col items-center">
                <div className="relative w-20 h-20">
                  <svg className="w-20 h-20 -rotate-90" viewBox="0 0 36 36">
                    <circle
                      cx="18"
                      cy="18"
                      r="15.5"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="3"
                    />
                    <circle
                      cx="18"
                      cy="18"
                      r="15.5"
                      fill="none"
                      stroke="#D4AF37"
                      strokeWidth="3"
                      strokeDasharray="70 100"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold text-brand-dark-blue">68%</span>
                  </div>
                </div>
                <span className="text-xs font-medium text-brand-dark-blue/60 mt-1">
                  <Trans>Readiness</Trans>
                </span>
              </div>

              {/* Linear bar placeholder */}
              <div className="flex-1 flex flex-col justify-center">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-brand-dark-blue/60">
                    <Trans>Communication Level</Trans>
                  </span>
                  <span className="text-sm font-bold text-brand-dark-blue">6.5/10</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-brand-gold to-brand-dark-blue rounded-full"
                    style={{ width: '65%' }}
                  />
                </div>
                <span className="text-xs text-brand-dark-blue/50 mt-1">Mid-Level</span>
              </div>
            </div>

            <div className="absolute inset-0 flex items-center justify-center">
              {onUnlock && (
                <button
                  onClick={onUnlock}
                  className="text-xs font-semibold text-brand-gold hover:text-brand-gold/80 transition-colors flex items-center gap-1"
                >
                  <Lock className="w-3 h-3" />
                  <Trans>Unlock to view</Trans>
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-brand-gold/20 bg-gradient-to-br from-brand-gold/5 to-white">
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-gold/10 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-brand-gold" />
          </div>
          <h3 className="text-sm font-bold text-brand-dark-blue">
            <Trans>Interview Readiness</Trans>
          </h3>
        </div>

        <div className="flex gap-6">
          {/* Circular gauge */}
          <div className="flex flex-col items-center">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 -rotate-90" viewBox="0 0 36 36">
                <circle
                  cx="18"
                  cy="18"
                  r="15.5"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="3"
                />
                <circle
                  cx="18"
                  cy="18"
                  r="15.5"
                  fill="none"
                  stroke={readiness >= 65 ? '#22c55e' : readiness >= 50 ? '#D4AF37' : '#ef4444'}
                  strokeWidth="3"
                  strokeDasharray={`${readiness} 100`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold text-brand-dark-blue">{readiness}%</span>
              </div>
            </div>
            <span className="text-xs font-medium text-brand-dark-blue/60 mt-1">
              {getReadinessLabel(readiness)}
            </span>
          </div>

          {/* Linear bar for seniority */}
          <div className="flex-1 flex flex-col justify-center">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-brand-dark-blue/60">
                <Trans>Communication Level</Trans>
              </span>
              <span className="text-sm font-bold text-brand-dark-blue">{seniority}/10</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-brand-gold to-brand-dark-blue rounded-full transition-all duration-500"
                style={{ width: `${seniority * 10}%` }}
              />
            </div>
            <span className="text-xs text-brand-dark-blue/50 mt-1">
              {getSeniorityLabel(seniority)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
